/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { DegreePlan_LearnerAdmissionStatus as DegreePlanLearnerAdmissionStatus } from '__generated__/graphql-types';

import { LoadingSection, Typography, breakpoints } from '@coursera/cds-core';

import CurriculumsRenderer from 'bundles/degree-home/components/v3-dpm/components/CurriculumRenderer';
import ErrorCard from 'bundles/degree-home/components/v3-dpm/components/ErrorCard';
import type {
  UseCurriculum_DegreeCurriculumFragment as DegreeCurriculumFragment,
  UseCurriculum_PbaCurriculumFragment as PbaCurriculumFragment,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCurriculum';
import { isDegreeCurriculum, isPbaCurriculum } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import useCurriculum from 'bundles/degree-home/components/v3-dpm/lib/useCurriculum';
import { Card } from 'bundles/program-loggedin-shared/components/Card';

import _t from 'i18n!nls/degree-home';

const styles = {
  curriculumTitle: () => css`
    margin-bottom: var(--cds-spacing-400);
    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-300);
    }
  `,
};

const Curriculum = () => {
  const curriculumGql = useCurriculum();
  if (!curriculumGql) {
    return null;
  }

  const isLearnerNotEnrolled =
    curriculumGql?.data?.Degree?.queryById?.curriculum?.learnerAdmissionStatus ===
    DegreePlanLearnerAdmissionStatus.NotEnrolled;
  const curriculumsData: (DegreeCurriculumFragment | PbaCurriculumFragment)[] =
    curriculumGql?.data?.Degree?.queryById?.curriculum?.curriculums || [];
  const degreeCurriculum = curriculumsData.find((curr) => isDegreeCurriculum(curr)) as
    DegreeCurriculumFragment | undefined;
  const pbaCurriculum = curriculumsData.find((curr) => isPbaCurriculum(curr)) as PbaCurriculumFragment | undefined;
  const curriculums: (DegreeCurriculumFragment | PbaCurriculumFragment)[] = degreeCurriculum ? [degreeCurriculum] : [];

  if (pbaCurriculum) {
    curriculums.unshift(pbaCurriculum);
  }

  return (
    <div>
      <div css={styles.curriculumTitle}>
        <Typography variant="h1semibold">{_t('Curriculum')}</Typography>
      </div>
      {curriculumGql.loading && !curriculumGql.data && (
        <Card>
          <LoadingSection label={_t('Fetching data...')} />
        </Card>
      )}
      {(curriculumGql.error || isLearnerNotEnrolled) && <ErrorCard />}
      {curriculumGql.data && !isLearnerNotEnrolled && <CurriculumsRenderer curriculumsData={curriculums} />}
    </div>
  );
};

export default Curriculum;
